import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { fetchAllKokkis, Kokki } from '../api/kokki';
import {
    BotDifficulty,
    BotProfile,
    BotProfilePayload,
    createBotProfile,
    deleteBotProfile,
    fetchBotProfiles,
    updateBotProfile,
} from '../api/bots';

const C = { primary: '#10b981', secondary: '#3b82f6', danger: '#ef4444', panel: '#1e293b' };
const inputStyle: CSSProperties = { width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f1f5f9', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle: CSSProperties = { color: '#94a3b8', fontSize: '12px', marginBottom: '6px', display: 'block', fontWeight: '600' };
const btnStyle = (bg: string, color = '#fff'): CSSProperties => ({ padding: '10px 18px', background: bg, color, border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' });

type BotFormState = {
    name: string;
    difficulty: BotDifficulty;
    isActive: boolean;
    gameTypes: string[];
    kokkiId: number | '';
    accuracyOverride: number;
    baseReactionMs: number;
    readingMsPerChar: number;
    minActionDelayMs: number;
    maxActionDelayMs: number;
    timingJitterPct: number;
    powerupUseChance: number;
    targetLeaderChance: number;
    bankChance: number;
    riskTolerance: number;
    notes: string;
    powerupWeightsJson: string;
};

const DIFFICULTY_PRESETS: Record<BotDifficulty, Omit<BotFormState, 'name' | 'difficulty' | 'isActive' | 'gameTypes' | 'kokkiId' | 'notes' | 'powerupWeightsJson'>> = {
    easy: {
        accuracyOverride: 0.35,
        baseReactionMs: 1800,
        readingMsPerChar: 65,
        minActionDelayMs: 3500,
        maxActionDelayMs: 15000,
        timingJitterPct: 0.35,
        powerupUseChance: 0.1,
        targetLeaderChance: 0.35,
        bankChance: 0.35,
        riskTolerance: 0.35,
    },
    medium: {
        accuracyOverride: 0.62,
        baseReactionMs: 1200,
        readingMsPerChar: 42,
        minActionDelayMs: 2000,
        maxActionDelayMs: 10000,
        timingJitterPct: 0.25,
        powerupUseChance: 0.22,
        targetLeaderChance: 0.6,
        bankChance: 0.55,
        riskTolerance: 0.5,
    },
    hard: {
        accuracyOverride: 0.86,
        baseReactionMs: 700,
        readingMsPerChar: 24,
        minActionDelayMs: 900,
        maxActionDelayMs: 6500,
        timingJitterPct: 0.18,
        powerupUseChance: 0.35,
        targetLeaderChance: 0.8,
        bankChance: 0.72,
        riskTolerance: 0.75,
    },
};

const difficultyColors: Record<BotDifficulty, string> = {
    easy: '#22c55e',
    medium: '#f59e0b',
    hard: '#ef4444',
};

const GAME_TYPE_OPTIONS = [
    { value: 'flipCardGame', label: 'Bandido' },
    { value: 'quizMarathon', label: 'Zooma' },
    { value: 'doubleDown', label: 'Rolla' },
];

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return 'Something went wrong';
};

const resolveUrl = (url: string | null | undefined): string => {
    if (!url) return '';
    if (url.startsWith('/')) return `${(process.env.REACT_APP_API_URL || 'https://api.playkokki.com').replace(/\/$/, '')}${url}`;
    return url;
};

const buildBlankForm = (kokkiId: number | '' = ''): BotFormState => ({
    name: '',
    difficulty: 'medium',
    isActive: true,
    gameTypes: ['quizMarathon'],
    kokkiId,
    ...DIFFICULTY_PRESETS.medium,
    notes: '',
    powerupWeightsJson: '',
});

const buildEditForm = (bot: BotProfile): BotFormState => ({
    name: bot.name,
    difficulty: bot.difficulty,
    isActive: bot.isActive,
    gameTypes: bot.gameTypes?.length ? bot.gameTypes : ['quizMarathon'],
    kokkiId: bot.kokkiId,
    accuracyOverride: bot.accuracyOverride ?? DIFFICULTY_PRESETS[bot.difficulty].accuracyOverride,
    baseReactionMs: bot.baseReactionMs,
    readingMsPerChar: bot.readingMsPerChar,
    minActionDelayMs: bot.minActionDelayMs,
    maxActionDelayMs: bot.maxActionDelayMs,
    timingJitterPct: bot.timingJitterPct,
    powerupUseChance: bot.powerupUseChance,
    targetLeaderChance: bot.targetLeaderChance,
    bankChance: bot.bankChance,
    riskTolerance: bot.riskTolerance,
    notes: bot.notes || '',
    powerupWeightsJson: bot.powerupWeights ? JSON.stringify(bot.powerupWeights, null, 2) : '',
});

const Stat = ({ label, value }: { label: string; value: string }) => (
    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '10px' }}>
        <span style={{ color: '#64748b', display: 'block', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 700 }}>{label}</span>
        <strong style={{ color: '#f1f5f9', fontSize: '13px' }}>{value}</strong>
    </div>
);

const BotFormModal = ({
    bot,
    kokkis,
    loading,
    onClose,
    onSave,
}: {
    bot: BotProfile | null;
    kokkis: Kokki[];
    loading: boolean;
    onClose: () => void;
    onSave: (payload: BotProfilePayload) => void;
}) => {
    const [form, setForm] = useState<BotFormState>(() => (bot ? buildEditForm(bot) : buildBlankForm(kokkis[0]?.id ?? '')));
    const [localError, setLocalError] = useState<string | null>(null);

    const setDifficulty = (difficulty: BotDifficulty) => {
        setForm(prev => ({ ...prev, difficulty, ...DIFFICULTY_PRESETS[difficulty] }));
    };

    const updateNumber = (key: keyof Pick<BotFormState, 'accuracyOverride' | 'baseReactionMs' | 'readingMsPerChar' | 'minActionDelayMs' | 'maxActionDelayMs' | 'timingJitterPct' | 'powerupUseChance' | 'targetLeaderChance' | 'bankChance' | 'riskTolerance'>, value: string) => {
        const parsed = Number(value);
        setForm(prev => ({ ...prev, [key]: Number.isFinite(parsed) ? parsed : 0 }));
    };

    const toggleGameType = (gameType: string) => {
        setForm(prev => {
            const nextGameTypes = prev.gameTypes.includes(gameType)
                ? prev.gameTypes.filter(value => value !== gameType)
                : [...prev.gameTypes, gameType];
            return { ...prev, gameTypes: nextGameTypes };
        });
    };

    const parsePowerupWeights = (): Record<string, number> | undefined => {
        if (!form.powerupWeightsJson.trim()) return undefined;
        const parsed: unknown = JSON.parse(form.powerupWeightsJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Powerup weights must be a JSON object');
        }
        const normalized: Record<string, number> = {};
        Object.entries(parsed).forEach(([key, value]) => {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
                throw new Error('Each powerup weight must be a non-negative number');
            }
            normalized[key] = value;
        });
        return normalized;
    };

    const submit = () => {
        try {
            setLocalError(null);
            if (!form.name.trim()) throw new Error('Bot name is required');
            if (!form.kokkiId) throw new Error('Choose the Kokki this bot will play with');
            if (form.gameTypes.length === 0) throw new Error('Choose at least one supported game');
            if (form.maxActionDelayMs < form.minActionDelayMs) throw new Error('Max delay must be greater than or equal to min delay');

            onSave({
                name: form.name.trim(),
                difficulty: form.difficulty,
                isActive: form.isActive,
                gameTypes: form.gameTypes,
                kokkiId: form.kokkiId,
                accuracyOverride: form.accuracyOverride,
                baseReactionMs: Math.round(form.baseReactionMs),
                readingMsPerChar: Math.round(form.readingMsPerChar),
                minActionDelayMs: Math.round(form.minActionDelayMs),
                maxActionDelayMs: Math.round(form.maxActionDelayMs),
                timingJitterPct: form.timingJitterPct,
                powerupUseChance: form.powerupUseChance,
                targetLeaderChance: form.targetLeaderChance,
                bankChance: form.bankChance,
                riskTolerance: form.riskTolerance,
                notes: form.notes,
                powerupWeights: parsePowerupWeights(),
            });
        } catch (error) {
            setLocalError(getErrorMessage(error));
        }
    };

    return (
        <div onClick={(event) => event.target === event.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '760px', maxHeight: '90vh', overflow: 'auto', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '6px' }}>{bot ? 'Edit Bot Profile' : 'Create Bot Profile'}</h2>
                <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>These values are captured when the host adds the bot to a standalone game session.</p>
                {localError && <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', padding: '10px 12px', borderRadius: '10px', color: '#fca5a5', fontSize: '13px', marginBottom: '16px' }}>{localError}</div>}

                <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 180px', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>Name *</label>
                            <input value={form.name} onChange={(event) => setForm(prev => ({ ...prev, name: event.target.value }))} style={inputStyle} placeholder="e.g. Nova Bot" />
                        </div>
                        <div>
                            <label style={labelStyle}>Difficulty</label>
                            <select value={form.difficulty} onChange={(event) => setDifficulty(event.target.value as BotDifficulty)} style={inputStyle}>
                                <option value="easy">Easy</option>
                                <option value="medium">Medium</option>
                                <option value="hard">Hard</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Status</label>
                            <select value={form.isActive ? 'active' : 'inactive'} onChange={(event) => setForm(prev => ({ ...prev, isActive: event.target.value === 'active' }))} style={inputStyle}>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>Kokki *</label>
                            <select value={form.kokkiId} onChange={(event) => setForm(prev => ({ ...prev, kokkiId: Number(event.target.value) }))} style={inputStyle}>
                                <option value="">Select Kokki</option>
                                {kokkis.map(kokki => <option key={kokki.id} value={kokki.id}>{kokki.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Supported games *</label>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {GAME_TYPE_OPTIONS.map(option => {
                                    const checked = form.gameTypes.includes(option.value);
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => toggleGameType(option.value)}
                                            style={{
                                                border: `1px solid ${checked ? C.primary : '#334155'}`,
                                                background: checked ? '#064e3b' : '#0f172a',
                                                color: checked ? '#d1fae5' : '#94a3b8',
                                                borderRadius: '999px',
                                                padding: '9px 12px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: 800,
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px' }}>
                        <div><label style={labelStyle}>Accuracy 0-1</label><input type="number" min="0" max="1" step="0.01" value={form.accuracyOverride} onChange={(event) => updateNumber('accuracyOverride', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Base reaction ms</label><input type="number" min="0" step="50" value={form.baseReactionMs} onChange={(event) => updateNumber('baseReactionMs', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Reading ms / char</label><input type="number" min="0" step="1" value={form.readingMsPerChar} onChange={(event) => updateNumber('readingMsPerChar', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Timing jitter 0-1</label><input type="number" min="0" max="1" step="0.01" value={form.timingJitterPct} onChange={(event) => updateNumber('timingJitterPct', event.target.value)} style={inputStyle} /></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px' }}>
                        <div><label style={labelStyle}>Min delay ms</label><input type="number" min="0" step="100" value={form.minActionDelayMs} onChange={(event) => updateNumber('minActionDelayMs', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Max delay ms</label><input type="number" min="0" step="100" value={form.maxActionDelayMs} onChange={(event) => updateNumber('maxActionDelayMs', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Powerup chance</label><input type="number" min="0" max="1" step="0.01" value={form.powerupUseChance} onChange={(event) => updateNumber('powerupUseChance', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Leader target chance</label><input type="number" min="0" max="1" step="0.01" value={form.targetLeaderChance} onChange={(event) => updateNumber('targetLeaderChance', event.target.value)} style={inputStyle} /></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                        <div><label style={labelStyle}>Bank chance</label><input type="number" min="0" max="1" step="0.01" value={form.bankChance} onChange={(event) => updateNumber('bankChance', event.target.value)} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Risk tolerance</label><input type="number" min="0" max="1" step="0.01" value={form.riskTolerance} onChange={(event) => updateNumber('riskTolerance', event.target.value)} style={inputStyle} /></div>
                    </div>

                    <div>
                        <label style={labelStyle}>Powerup weights JSON</label>
                        <textarea value={form.powerupWeightsJson} onChange={(event) => setForm(prev => ({ ...prev, powerupWeightsJson: event.target.value }))} style={{ ...inputStyle, minHeight: '82px', resize: 'vertical', fontFamily: 'monospace' }} placeholder={'{"zooma_skip_question": 1.2, "rolla_force_bank": 0.8}'} />
                    </div>

                    <div>
                        <label style={labelStyle}>Notes</label>
                        <textarea value={form.notes} onChange={(event) => setForm(prev => ({ ...prev, notes: event.target.value }))} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                    <button onClick={onClose} style={btnStyle('#334155', '#f1f5f9')}>Cancel</button>
                    <button onClick={submit} disabled={loading || !form.name.trim() || !form.kokkiId || form.gameTypes.length === 0} style={btnStyle(loading || !form.name.trim() || !form.kokkiId || form.gameTypes.length === 0 ? '#334155' : C.primary, loading ? '#64748b' : '#0f172a')}>{loading ? 'Saving...' : bot ? 'Update Bot' : 'Create Bot'}</button>
                </div>
            </div>
        </div>
    );
};

export default function BotsPage() {
    const [bots, setBots] = useState<BotProfile[]>([]);
    const [kokkis, setKokkis] = useState<Kokki[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [selectedBot, setSelectedBot] = useState<BotProfile | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [gameFilter, setGameFilter] = useState<'all' | 'flipCardGame' | 'quizMarathon' | 'doubleDown'>('all');

    const kokkiById = useMemo(() => new Map(kokkis.map(kokki => [kokki.id, kokki])), [kokkis]);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const [botProfiles, allKokkis] = await Promise.all([
                fetchBotProfiles(),
                fetchAllKokkis(),
            ]);
            setBots(botProfiles);
            setKokkis(allKokkis);
        } catch (loadError) {
            setError(getErrorMessage(loadError));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const flash = (message: string) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleSave = async (payload: BotProfilePayload) => {
        try {
            setSubmitting(true);
            setError(null);
            if (selectedBot) {
                await updateBotProfile(selectedBot.id, payload);
                flash('Bot profile updated');
            } else {
                await createBotProfile(payload);
                flash('Bot profile created');
            }
            setShowForm(false);
            setSelectedBot(null);
            await load();
        } catch (saveError) {
            setError(getErrorMessage(saveError));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (bot: BotProfile) => {
        if (!window.confirm(`Delete bot profile "${bot.name}"? Existing sessions keep their saved snapshot, but hosts cannot add it again.`)) return;
        try {
            setSubmitting(true);
            await deleteBotProfile(bot.id);
            flash('Bot profile deleted');
            await load();
        } catch (deleteError) {
            setError(getErrorMessage(deleteError));
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (bot: BotProfile) => {
        try {
            setSubmitting(true);
            await updateBotProfile(bot.id, { isActive: !bot.isActive });
            flash(bot.isActive ? 'Bot disabled' : 'Bot enabled');
            await load();
        } catch (toggleError) {
            setError(getErrorMessage(toggleError));
        } finally {
            setSubmitting(false);
        }
    };

    const filteredBots = bots.filter(bot => {
        if (filter === 'active') return bot.isActive;
        if (filter === 'inactive') return !bot.isActive;
        return true;
    }).filter(bot => {
        if (gameFilter === 'all') return true;
        return bot.gameTypes.includes(gameFilter);
    });

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '32px' }}>
            <div style={{ maxWidth: '1220px', margin: '0 auto' }}>
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>Bot Profiles</h1>
                        <p style={{ color: '#64748b', fontSize: '14px' }}>{bots.length} standalone bot profiles with dashboard-managed behavior.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <select value={gameFilter} onChange={(event) => setGameFilter(event.target.value as 'all' | 'flipCardGame' | 'quizMarathon' | 'doubleDown')} style={{ ...inputStyle, maxWidth: '160px' }}>
                            <option value="all">All Games</option>
                            <option value="flipCardGame">Bandido</option>
                            <option value="quizMarathon">Zooma</option>
                            <option value="doubleDown">Rolla</option>
                        </select>
                        <select value={filter} onChange={(event) => setFilter(event.target.value as 'all' | 'active' | 'inactive')} style={{ ...inputStyle, maxWidth: '160px' }}>
                            <option value="all">All Bots</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                        <button onClick={() => { setSelectedBot(null); setShowForm(true); }} style={{ padding: '12px 20px', background: C.primary, color: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Create Bot</button>
                    </div>
                </div>

                {success && <div style={{ background: '#064e3b', border: '1px solid #10b981', padding: '12px 16px', borderRadius: '12px', color: '#10b981', fontSize: '14px', marginBottom: '16px' }}>{success}</div>}
                {error && <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', padding: '12px 16px', borderRadius: '12px', color: '#fca5a5', fontSize: '14px', marginBottom: '16px' }}>{error}<button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', color: '#fca5a5', fontSize: '16px', cursor: 'pointer', float: 'right' }}>x</button></div>}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading...</div>
                ) : (
                    <div style={{ display: 'grid', gap: '14px' }}>
                        {filteredBots.map(bot => {
                            const kokki = bot.kokki || kokkiById.get(bot.kokkiId);
                            return (
                                <div key={bot.id} style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', border: `1px solid ${bot.isActive ? '#334155' : '#7f1d1d'}`, borderRadius: '14px', padding: '18px', display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr) auto', gap: '16px', alignItems: 'center' }}>
                                    <div style={{ width: '64px', height: '64px', borderRadius: '14px', overflow: 'hidden', background: '#0f172a', border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 800 }}>
                                        {kokki?.imageUrl ? <img src={resolveUrl(kokki.imageUrl)} alt={kokki.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (kokki?.name || 'B').slice(0, 1)}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                            <strong style={{ color: '#f1f5f9', fontSize: '18px' }}>{bot.name}</strong>
                                            <span style={{ color: difficultyColors[bot.difficulty], background: `${difficultyColors[bot.difficulty]}22`, border: `1px solid ${difficultyColors[bot.difficulty]}55`, borderRadius: '999px', padding: '4px 10px', fontSize: '11px', textTransform: 'uppercase', fontWeight: 800 }}>{bot.difficulty}</span>
                                            <span style={{ color: bot.isActive ? '#10b981' : '#fca5a5', background: bot.isActive ? '#064e3b' : '#7f1d1d', borderRadius: '999px', padding: '4px 10px', fontSize: '11px', textTransform: 'uppercase', fontWeight: 800 }}>{bot.isActive ? 'Active' : 'Inactive'}</span>
                                            {bot.gameTypes.map(gameType => (
                                                <span key={gameType} style={{ color: '#bfdbfe', background: '#1e3a8a', borderRadius: '999px', padding: '4px 10px', fontSize: '11px', textTransform: 'uppercase', fontWeight: 800 }}>
                                                    {GAME_TYPE_OPTIONS.find(option => option.value === gameType)?.label || gameType}
                                                </span>
                                            ))}
                                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Kokki: {kokki?.name || `#${bot.kokkiId}`}</span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '8px' }}>
                                            <Stat label="Accuracy" value={`${Math.round((bot.accuracyOverride ?? DIFFICULTY_PRESETS[bot.difficulty].accuracyOverride) * 100)}%`} />
                                            <Stat label="Reaction" value={`${bot.baseReactionMs}ms`} />
                                            <Stat label="Read" value={`${bot.readingMsPerChar}ms/ch`} />
                                            <Stat label="Delay" value={`${bot.minActionDelayMs}-${bot.maxActionDelayMs}ms`} />
                                            <Stat label="Powerups" value={`${Math.round(bot.powerupUseChance * 100)}%`} />
                                            <Stat label="Risk" value={`${Math.round(bot.riskTolerance * 100)}%`} />
                                        </div>
                                        {bot.notes && <p style={{ color: '#94a3b8', fontSize: '12px', margin: '10px 0 0' }}>{bot.notes}</p>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => handleToggleActive(bot)} disabled={submitting} style={{ ...btnStyle(bot.isActive ? '#475569' : C.primary, bot.isActive ? '#f1f5f9' : '#0f172a'), fontSize: '12px', padding: '8px 12px' }}>{bot.isActive ? 'Disable' : 'Enable'}</button>
                                        <button onClick={() => { setSelectedBot(bot); setShowForm(true); }} style={{ ...btnStyle(C.secondary), fontSize: '12px', padding: '8px 12px' }}>Edit</button>
                                        <button onClick={() => handleDelete(bot)} disabled={submitting} style={{ ...btnStyle(C.danger), fontSize: '12px', padding: '8px 12px' }}>Delete</button>
                                    </div>
                                </div>
                            );
                        })}
                        {filteredBots.length === 0 && <div style={{ color: '#64748b', fontSize: '14px', padding: '28px', textAlign: 'center', border: '1px dashed #334155', borderRadius: '14px' }}>No bot profiles match this filter.</div>}
                    </div>
                )}
            </div>

            {showForm && (
                <BotFormModal
                    bot={selectedBot}
                    kokkis={kokkis}
                    loading={submitting}
                    onClose={() => { setShowForm(false); setSelectedBot(null); }}
                    onSave={handleSave}
                />
            )}
        </div>
    );
}
